import { useEffect, useState, useCallback } from "react";
import client, { apiErr } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Plus, ImagePlus, AlertTriangle, CheckCircle2, Loader2, Clock, User, Archive, Trash2, RotateCcw, MapPin } from "lucide-react";
import { toast } from "sonner";
import { confirm } from "@/components/ConfirmDialog";
import { usePoll } from "@/hooks/usePoll";

const PRIO = { low: "bg-slate-100 text-slate-600", medium: "bg-amber-50 text-amber-700", high: "bg-red-50 text-red-700" };
const STAT = { open: "bg-red-50 text-red-700 border-red-200", in_progress: "bg-amber-50 text-amber-700 border-amber-200", resolved: "bg-emerald-50 text-emerald-700 border-emerald-200", archived: "bg-slate-100 text-slate-500 border-slate-200" };

function Photo({ id, big }) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let u;
    client.get(`/files/${id}/download`, { responseType: "blob" }).then((r) => {
      u = URL.createObjectURL(r.data); setUrl(u);
    }).catch(() => {});
    return () => u && URL.revokeObjectURL(u);
  }, [id]);
  const sz = big ? "h-40 w-40" : "h-16 w-16";
  if (!url) return <div className={`${sz} rounded bg-slate-100 animate-pulse`} />;
  return <img src={url} alt="evidence" data-testid={`issue-photo-${id}`} className={`${sz} rounded object-cover border border-slate-200`} />;
}

export default function Issues() {
  const { t } = useI18n();
  const { can } = useAuth();
  const [issues, setIssues] = useState([]);
  const [sites, setSites] = useState([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ siteId: "", title: "", description: "", priority: "medium", photoIds: [] });
  const [detail, setDetail] = useState(null);
  const [showArchived, setShowArchived] = useState(false);

  const load = useCallback(() => {
    client.get("/issues").then((r) => setIssues(r.data)).catch(() => {});
  }, []);
  usePoll(load, 15000);
  useEffect(() => {
    client.get("/sites").then((r) => { setSites(r.data); if (r.data[0]) setForm((f) => ({ ...f, siteId: r.data[0].id })); }).catch(() => {});
    load();
  }, [load]);

  const siteName = (id) => sites.find((s) => s.id === id)?.name || id;

  const uploadPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    if (form.siteId) fd.append("siteId", form.siteId);
    try {
      const { data } = await client.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, photoIds: [...f.photoIds, data.id] }));
      toast.success("Photo attached");
    } catch (err) { toast.error(apiErr(err.response?.data?.detail)); }
  };

  const create = async () => {
    setBusy(true);
    try {
      await client.post("/issues", form);
      toast.success("Issue reported");
      setOpen(false);
      setForm({ siteId: sites[0]?.id || "", title: "", description: "", priority: "medium", photoIds: [] });
      load();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  const setStatus = async (id, status) => {
    try { await client.patch(`/issues/${id}`, { status }); toast.success(t(`status.${status}`, status)); setDetail(null); load(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const del = async (i) => {
    if (!(await confirm({ title: t("issue.delTitle", "Delete issue"), description: `${t("issue.delConfirm", "Permanently delete")} "${i.title}"?`, confirmText: t("common.delete", "Delete"), destructive: true }))) return;
    try { await client.delete(`/issues/${i.id}`); toast.success(t("issue.deleted", "Issue deleted")); setDetail(null); load(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const shown = issues.filter((i) => (showArchived ? true : i.status !== "archived"));

  return (
    <div className="space-y-6" data-testid="issues-page">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-display text-2xl font-bold text-brand-ink">{t("issue.title", "Issues")}</h2>
          <p className="text-sm text-slate-500">{t("issue.subtitle", "Report and resolve problems with photo evidence.")}</p>
        </div>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-1.5 text-sm text-slate-600 cursor-pointer">
            <input type="checkbox" data-testid="issues-show-archived" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} />
            {t("issue.showArchived", "Show archived")}
          </label>
          <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="issue-create-button" className="bg-brand hover:bg-brand-dark">
              <Plus className="h-4 w-4 mr-2" /> {t("issue.new", "Report issue")}
            </Button>
          </DialogTrigger>
          <DialogContent data-testid="issue-create-dialog">
            <DialogHeader><DialogTitle>{t("issue.new", "Report issue")}</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>{t("msg.selectSite", "Site")}</Label>
                <Select value={form.siteId} onValueChange={(v) => setForm({ ...form, siteId: v })}>
                  <SelectTrigger data-testid="issue-site-select" className="mt-1.5"><SelectValue /></SelectTrigger>
                  <SelectContent>{sites.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>{t("issue.fieldTitle", "Title")}</Label><Input data-testid="issue-title-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1.5" /></div>
              <div><Label>{t("issue.fieldDesc", "Description")}</Label><Textarea data-testid="issue-desc-input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1.5" /></div>
              <div>
                <Label>{t("issue.priority", "Priority")}</Label>
                <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                  <SelectTrigger data-testid="issue-priority-select" className="mt-1.5"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">{t("prio.low", "Low")}</SelectItem>
                    <SelectItem value="medium">{t("prio.medium", "Medium")}</SelectItem>
                    <SelectItem value="high">{t("prio.high", "High")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>{t("issue.photos", "Photo evidence")}</Label>
                <div className="mt-1.5 flex items-center gap-3 flex-wrap">
                  {form.photoIds.map((id) => <Photo key={id} id={id} />)}
                  <label className="h-16 w-16 rounded border-2 border-dashed border-slate-300 flex items-center justify-center cursor-pointer hover:border-brand" data-testid="issue-photo-upload">
                    <ImagePlus className="h-5 w-5 text-slate-400" />
                    <input type="file" accept="image/*" className="hidden" onChange={uploadPhoto} />
                  </label>
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setOpen(false)}>{t("common.cancel","Cancel")}</Button>
              <Button data-testid="issue-save-btn" onClick={create} disabled={busy || !form.title || !form.siteId} className="bg-brand hover:bg-brand-dark">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : t("issue.new", "Report issue")}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {shown.length === 0 ? (
        <Card className="p-10 text-center text-slate-400" data-testid="issues-empty"><AlertTriangle className="h-8 w-8 mx-auto mb-2" />{t("issue.none", "No issues logged.")}</Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {shown.map((i) => (
            <Card key={i.id} data-testid={`issue-card-${i.id}`} onClick={() => setDetail(i)} className="p-5 border-slate-200 cursor-pointer hover:border-brand/40 hover:shadow-sm transition">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <h3 className="font-semibold text-slate-900">{i.title}</h3>
                  <p className="text-xs text-slate-500 mt-0.5 flex flex-wrap items-center gap-x-2">
                    <span className="inline-flex items-center gap-1"><User className="h-3 w-3" />{i.reporterName}</span>
                    <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" />{i.createdAt ? new Date(i.createdAt).toLocaleString() : "—"}</span>
                  </p>
                </div>
                <div className="flex flex-col items-end gap-1 shrink-0">
                  <Badge variant="outline" className={STAT[i.status]}>{t(`status.${i.status}`, i.status)}</Badge>
                  <Badge className={PRIO[i.priority]}>{t(`prio.${i.priority}`, i.priority)}</Badge>
                </div>
              </div>
              {i.description && <p className="text-sm text-slate-600 mt-2 line-clamp-2">{i.description}</p>}
              {i.photoIds?.length > 0 && <div className="flex gap-2 mt-3">{i.photoIds.slice(0, 4).map((pid) => <Photo key={pid} id={pid} />)}</div>}
            </Card>
          ))}
        </div>
      )}

      <Dialog open={!!detail} onOpenChange={(o) => { if (!o) setDetail(null); }}>
        <DialogContent data-testid="issue-detail-dialog" className="max-w-lg max-h-[90vh] overflow-y-auto">
          {detail && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2"><AlertTriangle className="h-5 w-5 text-brand" />{detail.title}</DialogTitle>
              </DialogHeader>
              <div className="space-y-3">
                <div className="flex flex-wrap gap-2">
                  <Badge variant="outline" className={STAT[detail.status]}>{t(`status.${detail.status}`, detail.status)}</Badge>
                  <Badge className={PRIO[detail.priority]}>{t(`prio.${detail.priority}`, detail.priority)}</Badge>
                </div>
                <div className="text-sm text-slate-600 space-y-1.5 border-y border-slate-100 py-3">
                  <p className="flex items-center gap-2"><User className="h-4 w-4 text-slate-400" />{t("issue.reportedBy", "Reported by")}: <span className="font-medium text-slate-800">{detail.reporterName}</span></p>
                  <p className="flex items-center gap-2"><Clock className="h-4 w-4 text-slate-400" />{t("issue.reportedAt", "Reported at")}: <span className="font-medium text-slate-800">{detail.createdAt ? new Date(detail.createdAt).toLocaleString() : "—"}</span></p>
                  <p className="flex items-center gap-2"><MapPin className="h-4 w-4 text-slate-400" />{detail.siteId ? siteName(detail.siteId) : t("issue.jobIssue", "Job issue")}</p>
                </div>
                {detail.description && <div><p className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-1">{t("issue.fieldDesc", "Description")}</p><p className="text-sm text-slate-700 whitespace-pre-wrap">{detail.description}</p></div>}
                {detail.photoIds?.length > 0 && <div><p className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-1.5">{t("issue.photos", "Photo evidence")}</p><div className="flex gap-2 flex-wrap">{detail.photoIds.map((pid) => <Photo key={pid} id={pid} big />)}</div></div>}
              </div>
              {can("issue.manage") && (
                <DialogFooter className="flex-wrap gap-2">
                  {detail.status !== "resolved" && <Button size="sm" variant="outline" data-testid="issue-detail-resolve" onClick={() => setStatus(detail.id, "resolved")}><CheckCircle2 className="h-4 w-4 mr-1.5" />{t("issue.resolve", "Resolve")}</Button>}
                  {(detail.status === "resolved" || detail.status === "archived") && <Button size="sm" variant="outline" data-testid="issue-detail-reopen" onClick={() => setStatus(detail.id, "open")}><RotateCcw className="h-4 w-4 mr-1.5" />{t("issue.reopen", "Reopen")}</Button>}
                  {detail.status !== "archived" && <Button size="sm" variant="outline" data-testid="issue-detail-archive" onClick={() => setStatus(detail.id, "archived")}><Archive className="h-4 w-4 mr-1.5" />{t("issue.archive", "Archive")}</Button>}
                  <Button size="sm" variant="outline" className="text-red-600 border-red-200 hover:bg-red-50" data-testid="issue-detail-delete" onClick={() => del(detail)}><Trash2 className="h-4 w-4 mr-1.5" />{t("common.delete", "Delete")}</Button>
                </DialogFooter>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
